/**
 * Book parsers - browser-native, no Node deps.
 *
 * EPUB: manual parse via JSZip + DOMParser. An EPUB is just a ZIP. This
 * approach is what foliate-js does (the reference reliable JS reader). No
 * silent failures like epubjs - every error is surfaced.
 *
 * PDF: pdfjs-dist with cMapUrl + standardFontDataUrl (ships /cmaps/ + /standard_fonts/
 * from public/). Without these, books with embedded fonts return empty text.
 *
 * MD: simple heading split + markdown stripping.
 * TXT: paragraph split by blank lines.
 */

export type ParsedBook = {
  title: string;
  author: string;
  lang?: string;
  pages: number;
  chapters: { idx: number; title: string; text: string }[];
  coverDataUrl?: string | null;
  kind: 'epub' | 'pdf' | 'txt' | 'md';
};

export type ParseProgress = (p: { phase: string; pct?: number }) => void;

export async function parseBook(file: File, onProgress?: ParseProgress): Promise<ParsedBook> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.epub')) return parseEpub(file, onProgress);
  if (name.endsWith('.pdf')) return parsePdf(file, onProgress);
  if (name.endsWith('.md') || name.endsWith('.markdown')) return parseMd(file, onProgress);
  if (name.endsWith('.txt')) return parseTxt(file, onProgress);
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  const hex = [...head].map((b) => b.toString(16).padStart(2, '0')).join('');
  if (hex.startsWith('504b0304')) return parseEpub(file, onProgress);
  if (hex.startsWith('25504446')) return parsePdf(file, onProgress);
  return parseTxt(file, onProgress);
}

/* ================== EPUB via JSZip + DOMParser ================== */

async function parseEpub(file: File, onProgress?: ParseProgress): Promise<ParsedBook> {
  onProgress?.({ phase: 'Unzipping EPUB...' });
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(await file.arrayBuffer());

  // 1) container.xml -> OPF path
  const containerFile = zip.file('META-INF/container.xml');
  if (!containerFile) throw new Error('Not a valid EPUB (missing META-INF/container.xml)');
  const containerXml = await containerFile.async('text');
  const containerDoc = new DOMParser().parseFromString(containerXml, 'application/xml');
  const rootfile = containerDoc.querySelector('rootfile');
  const opfPath = rootfile?.getAttribute('full-path');
  if (!opfPath) throw new Error('No rootfile in container.xml');

  const opfDir = opfPath.includes('/') ? opfPath.slice(0, opfPath.lastIndexOf('/') + 1) : '';

  // 2) OPF: metadata, manifest, spine
  onProgress?.({ phase: 'Reading manifest...' });
  const opfFile = zip.file(opfPath);
  if (!opfFile) throw new Error(`OPF not found at ${opfPath}`);
  const opfXml = await opfFile.async('text');
  const opfDoc = new DOMParser().parseFromString(opfXml, 'application/xml');

  // Metadata (Dublin Core)
  const getMeta = (name: string): string => {
    // try namespaced first
    const el = Array.from(opfDoc.getElementsByTagName('*')).find((e) => {
      const ln = e.localName?.toLowerCase();
      return ln === name.toLowerCase();
    });
    return el?.textContent?.trim() || '';
  };
  const title = getMeta('title') || file.name.replace(/\.epub$/i, '');
  const author = getMeta('creator') || 'Unknown';
  const language = getMeta('language') || undefined;

  // Manifest: id -> { href, mediaType, properties }
  const manifest = new Map<string, { id: string; href: string; mediaType?: string; properties?: string }>();
  opfDoc.querySelectorAll('manifest > item').forEach((item) => {
    const id = item.getAttribute('id') || '';
    const href = item.getAttribute('href') || '';
    if (!id || !href) return;
    manifest.set(id, {
      id,
      href: opfDir + href,
      mediaType: item.getAttribute('media-type') || undefined,
      properties: item.getAttribute('properties') || undefined,
    });
  });

  // Spine
  const spineIds: string[] = [];
  opfDoc.querySelectorAll('spine > itemref').forEach((ref) => {
    const idref = ref.getAttribute('idref');
    if (idref) spineIds.push(idref);
  });
  if (!spineIds.length) throw new Error('Spine is empty - no readable content');

  // 3) Cover image (EPUB 3 property, then EPUB 2 <meta name="cover">)
  onProgress?.({ phase: 'Extracting cover...' });
  let coverDataUrl: string | null = null;
  try {
    let coverItem: { href: string; mediaType?: string } | undefined;
    for (const it of manifest.values()) {
      if (it.properties?.includes('cover-image')) { coverItem = it; break; }
    }
    if (!coverItem) {
      const metaCover = opfDoc.querySelector('metadata > meta[name="cover"]')?.getAttribute('content');
      if (metaCover) {
        const it = manifest.get(metaCover);
        if (it) coverItem = it;
      }
    }
    if (!coverItem) {
      // Last resort: find image file named "cover*"
      for (const it of manifest.values()) {
        if (it.mediaType?.startsWith('image/') && /cover/i.test(it.href)) { coverItem = it; break; }
      }
    }
    if (coverItem) {
      const coverFile = zip.file(coverItem.href);
      if (coverFile) {
        const blob = await coverFile.async('blob');
        coverDataUrl = await blobToDataUrl(blob);
      }
    }
  } catch (e) { console.warn('[epub] cover failed', e); }

  // 4) TOC labels (nav.xhtml preferred, NCX fallback)
  const tocLabels = new Map<string, string>();
  try {
    const navItem = Array.from(manifest.values()).find((i) => i.properties?.includes('nav'));
    if (navItem) {
      const navHtml = await zip.file(navItem.href)?.async('text');
      if (navHtml) {
        const navDoc = new DOMParser().parseFromString(navHtml, 'application/xhtml+xml');
        navDoc.querySelectorAll('nav[*|type="toc"] a, nav a').forEach((a) => {
          const href = a.getAttribute('href');
          if (!href) return;
          const resolved = resolvePath(navItem.href, href.split('#')[0]);
          const label = a.textContent?.trim() || '';
          if (label && !tocLabels.has(resolved)) tocLabels.set(resolved, label);
        });
      }
    } else {
      // NCX
      const ncxItem = Array.from(manifest.values()).find((i) => i.mediaType === 'application/x-dtbncx+xml');
      if (ncxItem) {
        const ncxXml = await zip.file(ncxItem.href)?.async('text');
        if (ncxXml) {
          const ncxDoc = new DOMParser().parseFromString(ncxXml, 'application/xml');
          ncxDoc.querySelectorAll('navPoint').forEach((np) => {
            const label = np.querySelector('navLabel > text')?.textContent?.trim() || '';
            const src = np.querySelector('content')?.getAttribute('src');
            if (label && src) {
              const resolved = resolvePath(ncxItem.href, src.split('#')[0]);
              tocLabels.set(resolved, label);
            }
          });
        }
      }
    }
  } catch (e) { console.warn('[epub] toc failed', e); }

  // 5) Read each spine item
  onProgress?.({ phase: 'Reading chapters...' });
  const chapters: { idx: number; title: string; text: string }[] = [];
  for (let i = 0; i < spineIds.length; i++) {
    onProgress?.({ phase: `Reading chapter ${i + 1}/${spineIds.length}`, pct: Math.round((i / spineIds.length) * 100) });
    const id = spineIds[i];
    const item = manifest.get(id);
    if (!item) continue;
    try {
      const xhtml = await zip.file(item.href)?.async('text');
      if (!xhtml) continue;
      const doc = parseXhtml(xhtml);
      const text = extractBodyText(doc);
      if (text.trim().length < 80) continue;
      const heading = extractHeading(doc);
      const tocLabel = tocLabels.get(item.href);
      const rawTitle = heading || tocLabel || deriveTitle(item.href) || `Section ${chapters.length + 1}`;
      const title = cleanTitle(rawTitle);
      const fm = isFrontMatter(title, item.href, text, chapters.length);
      chapters.push({ idx: chapters.length, title: fm ? `${title} (front matter)` : title, text });
    } catch (e) {
      console.warn('[epub] chapter', id, 'failed', e);
    }
  }
  console.log('[epub] parsed', chapters.length, 'chapters from', spineIds.length, 'spine items');

  if (!chapters.length) throw new Error('No readable chapters found. The EPUB may be DRM-protected or encrypted.');

  const totalWords = chapters.reduce((a, c) => a + c.text.split(/\s+/).length, 0);
  const pages = Math.max(1, Math.round(totalWords / 300));

  return { title, author, lang: language, pages, chapters, coverDataUrl, kind: 'epub' };
}

function parseXhtml(xhtml: string): Document {
  // Try XML first (strict), fall back to HTML on malformed tags
  try {
    const doc = new DOMParser().parseFromString(xhtml, 'application/xhtml+xml');
    if (!doc.querySelector('parsererror')) return doc;
  } catch {}
  return new DOMParser().parseFromString(xhtml, 'text/html');
}

function extractHeading(doc: Document): string | null {
  const h = doc.querySelector('h1, h2, h3, h4');
  const t = h?.textContent?.trim();
  return t && t.length < 150 ? t : null;
}

const BLOCK_TAGS = new Set(['P', 'DIV', 'SECTION', 'ARTICLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'LI', 'FIGURE', 'FIGCAPTION', 'PRE']);

function extractBodyText(doc: Document): string {
  const body = doc.body || doc.documentElement;
  if (!body) return '';

  // Strip noise that's not main reading content. Same approach Pocket Reader,
  // Readwise, and Mozilla Readability take: remove footnotes, endnotes,
  // page-number markers, citation references, sidebars, images.
  body.querySelectorAll([
    'script', 'style', 'nav', 'noscript', 'header', 'footer', 'aside',
    'sup', 'sub',                          // citations / superscript refs
    'figure', 'img', 'svg', 'picture',     // images break TTS prose
    '[hidden]', '[aria-hidden="true"]',
    '[role="note"]', '[role="navigation"]', '[role="complementary"]',
    '[class*="footnote" i]', '[class*="endnote" i]',
    '[class*="page-num" i]', '[class*="page_num" i]', '[class*="pageno" i]', '[class*="folio" i]',
    '[class*="noteref" i]', '[class*="footref" i]', '[class*="ref" i]:not(p):not(div)',
    '[class*="sidebar" i]', '[class*="callout" i]',
    'a[href*="footnote" i]', 'a[href*="endnote" i]', 'a[role="doc-noteref"]',
  ].join(',')).forEach((n) => n.remove());

  // EPUB 3 semantic types: epub:type="pagebreak|footnote|endnote|noteref|toc"
  Array.from(body.getElementsByTagName('*')).forEach((el) => {
    const t = el.getAttribute('epub:type')
      || el.getAttributeNS('http://www.idpf.org/2007/ops', 'type')
      || el.getAttribute('data-epub-type');
    if (t && /(pagebreak|footnote|endnote|noteref|footref|toc|index)/i.test(t)) el.remove();
  });
  const walk = (node: Node): string => {
    let out = '';
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) {
        out += (child.textContent || '').replace(/\s+/g, ' ');
      } else if (child.nodeType === 1) {
        const el = child as Element;
        const tag = el.tagName?.toUpperCase();
        if (tag === 'BR') out += '\n';
        else if (BLOCK_TAGS.has(tag)) {
          const inner = walk(el).trim();
          if (inner) out += '\n\n' + inner + '\n\n';
        } else {
          out += walk(el);
        }
      }
    }
    return out;
  };
  let text = walk(body);
  text = text
    .normalize('NFC')
    .replace(/[\u0000-\u001f\u007f-\u009f\u00ad\u200b-\u200d\ufeff]/g, '') // control + zero-width
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Per-paragraph cleanup: drop stray-number-only lines, strip orphan citation
  // numbers like "diaphragm 7,174" (left over after <sup> removal). Approach
  // mirrors how Reader Mode strips page-number / noteref pollution.
  text = text
    .split(/\n+/)
    .map((line) => {
      const trimmed = line.trim();
      // Remove lines that are JUST page-number style: "32" or "32 96" or "5,861 195 96"
      if (/^\s*[\d,]+(?:\s+[\d,]+)*\s*$/.test(trimmed)) return '';
      // Strip standalone numbers in the middle of a sentence that are the
      // detritus of removed <sup>: "the diaphragm 7,174 increases" -> "the diaphragm increases".
      // Conservative: only strip 1-5 digit numbers (or with a single comma) that sit between
      // a lowercase letter and another word start - these are almost always footnote refs.
      return trimmed.replace(/([a-z]) ([\d,]{1,7}) (?=[a-z])/g, '$1 ');
    })
    .filter((l) => l.length > 0)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');

  return text;
}

function deriveTitle(href: string): string | null {
  const base = href.split('/').pop()?.replace(/\.x?html?$/i, '');
  if (!base) return null;
  const m = base.match(/(\d+)/);
  if (m) return `Chapter ${parseInt(m[1], 10)}`;
  return base.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function cleanTitle(t: string): string {
  return t
    .normalize('NFC')
    // strip control chars, zero-width spaces, BOM, soft hyphens
    .replace(/[\u0000-\u001f\u007f-\u009f\u00ad\u200b-\u200d\ufeff]/g, '')
    // collapse whitespace and decorative bullets used as separators
    .replace(/\s+/g, ' ')
    .replace(/[\u2022\u00b7\u2027]+/g, ' · ')
    .replace(/^\s*chapter\s+/i, 'Chapter ')
    .trim()
    .slice(0, 100);
}

const FRONT_MATTER_TITLE = /^(cover|title\s*page|copyright|dedication|epigraph|table\s*of\s*contents|contents|toc|colophon|imprint|advance\s*praise|praise|also\s*by|about\s*the\s*author|notes?|references?|bibliography|index|acknowledg|halftitle)\b/i;
const FRONT_MATTER_HREF = /\b(cover|title|copyright|nav|toc|contents|dedication|colophon)\b/i;

function isFrontMatter(title: string, href: string, text: string, idx: number): boolean {
  if (idx > 6) return false; // only check the first few
  if (FRONT_MATTER_TITLE.test(title)) return true;
  if (FRONT_MATTER_HREF.test(href) && text.length < 1500) return true;
  return false;
}

function resolvePath(base: string, rel: string): string {
  const stack = base.split('/').slice(0, -1);
  for (const part of rel.split('/')) {
    if (part === '..') stack.pop();
    else if (part !== '.' && part !== '') stack.push(part);
  }
  return stack.join('/');
}

/* ================== PDF with cMaps ================== */

async function parsePdf(file: File, onProgress?: ParseProgress): Promise<ParsedBook> {
  onProgress?.({ phase: 'Opening PDF...' });
  // @ts-ignore
  const pdfjs: any = await import('pdfjs-dist/build/pdf.mjs');
  // @ts-ignore
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const buf = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({
    data: buf,
    cMapUrl: '/cmaps/',
    cMapPacked: true,
    standardFontDataUrl: '/standard_fonts/',
    useSystemFonts: false,
  }).promise;
  const numPages = pdf.numPages;

  const meta = await pdf.getMetadata().catch(() => ({} as any));
  const info: any = (meta as any)?.info || {};
  const title = (info.Title || file.name.replace(/\.pdf$/i, '')).trim();
  const author = (info.Author || 'Unknown').toString().trim();

  onProgress?.({ phase: 'Rendering cover...' });
  let coverDataUrl: string | null = null;
  try {
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1.0 });
    const scale = Math.min(480 / viewport.width, 720 / viewport.height);
    const v2 = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(v2.width);
    canvas.height = Math.round(v2.height);
    const ctx = canvas.getContext('2d')!;
    await page.render({ canvasContext: ctx, viewport: v2 }).promise;
    coverDataUrl = canvas.toDataURL('image/jpeg', 0.7);
  } catch (e) { console.warn('[pdf] cover render failed', e); }

  onProgress?.({ phase: 'Extracting text...' });
  const chapters: { idx: number; title: string; text: string }[] = [];
  const CHUNK = 10;
  let buffer = '';
  let chapterStartPage = 1;
  for (let p = 1; p <= numPages; p++) {
    if (p % 2 === 0) onProgress?.({ phase: `Extracting text ${p}/${numPages}`, pct: Math.round((p / numPages) * 100) });
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    let lineY = NaN;
    let pageText = '';
    for (const it of content.items as any[]) {
      const y = it.transform?.[5];
      if (typeof y === 'number') {
        if (!isNaN(lineY) && Math.abs(y - lineY) > 2) pageText += '\n';
        lineY = y;
      }
      pageText += (it.str || '') + (it.hasEOL ? '\n' : ' ');
    }
    pageText = pageText.replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n\n').trim();
    if (pageText) buffer += (buffer ? '\n\n' : '') + pageText;
    if (p % CHUNK === 0 || p === numPages) {
      chapters.push({ idx: chapters.length, title: `Pages ${chapterStartPage}-${p}`, text: buffer });
      buffer = '';
      chapterStartPage = p + 1;
    }
  }
  console.log('[pdf] parsed', chapters.length, 'chapters,', numPages, 'pages');
  return { title, author, pages: numPages, chapters, coverDataUrl, kind: 'pdf' };
}

/* ================== Markdown ================== */

async function parseMd(file: File, onProgress?: ParseProgress): Promise<ParsedBook> {
  onProgress?.({ phase: 'Reading markdown...' });
  const text = await file.text();
  const name = file.name.replace(/\.(md|markdown)$/i, '');

  const lines = text.split(/\r?\n/);
  const chapters: { idx: number; title: string; text: string }[] = [];
  let curTitle = '';
  let curLines: string[] = [];
  const flush = () => {
    const body = curLines.join('\n').trim();
    if (body.length > 40) chapters.push({ idx: chapters.length, title: curTitle || `Part ${chapters.length + 1}`, text: stripMarkdown(body) });
  };
  for (const line of lines) {
    const h = line.match(/^(#{1,2})\s+(.+?)\s*#*\s*$/);
    if (h) {
      flush();
      curTitle = h[2].trim();
      curLines = [];
    } else {
      curLines.push(line);
    }
  }
  flush();
  if (!chapters.length) chapters.push({ idx: 0, title: 'Document', text: stripMarkdown(text) });

  let title = name;
  let author = 'Unknown';
  const fm = text.match(/^---\s*\n([\s\S]*?)\n---/);
  if (fm) {
    const t = fm[1].match(/^title:\s*(.+)$/mi);
    const a = fm[1].match(/^author:\s*(.+)$/mi);
    if (t) title = t[1].trim().replace(/^["']|["']$/g, '');
    if (a) author = a[1].trim().replace(/^["']|["']$/g, '');
  }

  const pages = Math.max(1, Math.round(text.split(/\s+/).length / 300));
  return { title, author, pages, chapters, coverDataUrl: null, kind: 'md' };
}

function stripMarkdown(s: string): string {
  return s
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/* ================== TXT ================== */

async function parseTxt(file: File, onProgress?: ParseProgress): Promise<ParsedBook> {
  onProgress?.({ phase: 'Reading text...' });
  const text = (await file.text()).replace(/\r\n/g, '\n');
  const name = file.name.replace(/\.txt$/i, '');
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim());
  const chapters: { idx: number; title: string; text: string }[] = [];
  let buf = '';
  for (let i = 0; i < paragraphs.length; i++) {
    buf += (buf ? '\n\n' : '') + paragraphs[i];
    if (buf.length > 5000 || i === paragraphs.length - 1) {
      chapters.push({ idx: chapters.length, title: `Part ${chapters.length + 1}`, text: buf });
      buf = '';
    }
  }
  if (!chapters.length) chapters.push({ idx: 0, title: 'Text', text });
  const pages = Math.max(1, Math.round(text.split(/\s+/).length / 300));
  return { title: name, author: 'Unknown', pages, chapters, coverDataUrl: null, kind: 'txt' };
}

/* ================== helpers ================== */

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}
